import React, { useMemo } from 'react';
import { Modal, Button, Tooltip, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import useAdMetrics from 'hooks/marketing/useAdMetrics';
import isNil from 'lodash/isNil';
import s from './s.module.less';
import useMarketingReport from 'hooks/marketing/useMarketingReport';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';

interface ProListingReportModalProps {
    open: boolean;
    onCancel: () => void;
    onAddAvailability: () => void;
}

type PerformanceLevel = 'TOP' | 'MID' | 'LOW';

interface ReportCardConfig {
    insightTitle: string;
    insightDescription: string | React.ReactNode;
    tipTitle: string;
    tipDescription: React.ReactNode;
}

const ProListingReportModal = ({ open, onCancel, onAddAvailability }: ProListingReportModalProps) => {
    const [report, reportLoading] = useMarketingReport();

    const performanceLevel = report?.report.lastTwoWeekPerformanceLevel;
    const metrics = report?.summary || {};

    const reportCardContent: Record<PerformanceLevel, ReportCardConfig> = {
        TOP: {
            insightTitle: "You're clearly in the Top 20%",
            insightDescription: "You've been attracting more patients than most providers over the past two weeks. This puts you in the top 20% on Klarity Health. Keep up the great work to maintain your strong results.",
            tipTitle: 'Keep the momentum going',
            tipDescription: (
                <>
                    <p className={s.tipText}>You&apos;re doing great! To stay in the top 20%:</p>
                    <ul className={s.tipList}>
                        <li>Keep your bid competitive so you stay highly visible in search results.</li>
                        <li>Keep your calendar updated with at least 20 open hours this week so patients can easily book with you.</li>
                        <li>Update your calendar regularly. Make sure you have <strong>at least 20 open hours in the next 5 business days</strong> so patients can easily book and you get the best return on your marketing investment.</li>
                    </ul>
                </>
            ),
        },
        MID: {
            insightTitle: "You're making steady progress",
            insightDescription: "Your profile is starting to connect with more patients, and your bookings show that Pro-Listings is working for you. With some adjustments to your bid and calendar, you can build on this momentum and reach even more patients. High-performing Pro-Listing providers see up to 10 additional new patients each week after joining.",
            tipTitle: 'Build on your progress',
            tipDescription: (
                <>
                    <p className={s.tipText}>You&apos;re on the right track. Small updates can boost your results:</p>
                    <ul className={s.tipList}>
                        <li>Consider a modest bid increase to capture more impressions and convert them into bookings.</li>
                        <li>Update your calendar regularly. Make sure you have <strong>at least 20 open hours in the next 5 business days</strong> so patients can easily book and you get the best return on your marketing investment.</li>
                    </ul>
                </>
            ),
        },
        LOW: {
            insightTitle: "There's plenty of room to grow",
            insightDescription: (
                <>
                    <p>It's normal to start with just a few bookings — many providers do. The good news is that performance often improves quickly with a few small adjustments. Try increasing your bid or experimenting with higher visibility tiers to attract more patients. High-performing Pro-Listing providers see up to <strong>10 additional new patients each week</strong> after joining.</p>
                </>
            ),
            tipTitle: 'Quick wins to boost bookings',
            tipDescription: (
                <>
                    <p className={s.tipText}>You&apos;re just getting started, and every small improvement helps:</p>
                    <ul className={s.tipList}>
                        <li>Increase your bid to compete for more impressions and catch up to the average.</li>
                        <li>Update your calendar regularly. Make sure you have <strong>at least 20 open hours in the next 5 business days</strong> so patients can easily book and you get the best return on your marketing investment.</li>
                    </ul>
                </>
            ),
        },
    };

    const currentConfig = reportCardContent[performanceLevel || 'MID'];

    const formatChange = (value: number) => {
        if (value === 0) return '— 0%';
        const sign = value > 0 ? '↑' : '↓';
        return `${sign} ${Math.abs(value)}%`;
    };

    const handleAddAvailability = () => {
        onAddAvailability();
        onCancel();
    };

    const tooltips = {
        bookings: 'The number of new patient appointments booked through your Pro-Listings',
        impressions: 'How many times your profile was shown to patients in Klarity search results.',
        impressionShare: 'The percentage of total impressions your profile received compared to all impressions received by providers similar to you in terms of specialty and state.',
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={1160}
            title={null}
            className={s.reportModal}
        >
            <div className={s.modalContent}>
                <h2 className={s.modalTitle}>Pro-Listings report</h2>
                <p className={s.modalSubtitle}>
                    Track your Pro-Listing performance and insights to help you attract more patients. Reports update every two weeks to show how you compare with similar providers.
                </p>

                {/* Performance Insight Card */}
                <Spin spinning={reportLoading}>
                    <div className={s.insightCard}>
                        <svg className={s.insightIcon} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_5621_17059)">
                                <path d="M0.75 23.25H23.25M4.18 9.24L20.25 1.94M20.25 1.94L19.06 5.13M20.25 1.94L17.07 0.75M6 16.25H3C2.80189 16.2526 2.61263 16.3324 2.47253 16.4725C2.33244 16.6126 2.25259 16.8019 2.25 17V23.25H6.75V17C6.74741 16.8019 6.66756 16.6126 6.52747 16.4725C6.38737 16.3324 6.19811 16.2526 6 16.25ZM13.5 12.25H10.5C10.3019 12.2526 10.1126 12.3324 9.97253 12.4725C9.83244 12.6126 9.75259 12.8019 9.75 13V23.25H14.25V13C14.2474 12.8019 14.1676 12.6126 14.0275 12.4725C13.8874 12.3324 13.6981 12.2526 13.5 12.25ZM21 8.25H18C17.8019 8.25259 17.6126 8.33244 17.4725 8.47253C17.3324 8.61263 17.2526 8.80189 17.25 9V23.25H21.75V9C21.7474 8.80189 21.6676 8.61263 21.5275 8.47253C21.3874 8.33244 21.1981 8.25259 21 8.25Z" stroke="#0E62F4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_5621_17059">
                                    <rect width="24" height="24" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                        <div className={s.cardHeader}>
                            <div className={s.cardLabel}>Performance insight</div>
                            <h3 className={s.cardTitle}>{currentConfig.insightTitle}</h3>
                            <p className={s.cardDescription}>{currentConfig.insightDescription}</p>
                        </div>
                    </div>

                    {/* Growth Tips Card */}
                    <div className={s.tipsCard}>
                        <svg className={s.tipsIcon} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M20 10C20 13.404 17.874 16.311 14.878 17.467H9.122C6.126 16.311 4 13.404 4 10C4 5.5815 7.5815 2 12 2C16.4185 2 20 5.5815 20 10Z" stroke="#7C3AED" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M14.878 17.467L14.538 21.542C14.5275 21.6669 14.4705 21.7832 14.3782 21.868C14.286 21.9528 14.1653 21.9999 14.04 22H9.96C9.83462 21.9999 9.71385 21.9528 9.62161 21.8678C9.52937 21.7829 9.47241 21.6664 9.462 21.5415L9.122 17.4665M9 8.5V11.5L12 10L15 11.5V8.5" stroke="#7C3AED" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <div className={s.cardHeader}>
                            <span className={s.cardLabel}>Growth tips</span>
                            <h3 className={s.cardTitle}>{currentConfig.tipTitle}</h3>
                            <div className={s.cardDescription}>{currentConfig.tipDescription}</div>
                            <Button
                                type="primary"
                                size="large"
                                className={s.addAvailabilityBtn}
                                onClick={handleAddAvailability}
                            >
                                Add availability
                            </Button>
                        </div>
                    </div>

                    {/* Performance Metrics */}
                    <h3 className={s.performanceTitle}>Your performance in the last 2 weeks</h3>
                    <div className={s.metricsGrid}>
                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Bookings</span>
                                <Tooltip title={tooltips.bookings} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.totalBookings || 0}</span>
                                    {!isNil(metrics.previousTotalBookings) && (
                                        <span className={s.baseValue}>from {metrics.previousTotalBookings}</span>
                                    )}
                                </div>
                                {!isNil(metrics.totalBookingsChangeRate) && (
                                    <div className={`${s.changeValue} ${metrics.totalBookingsChangeRate > 0 && s.positive || metrics.totalBookingsChangeRate < 0 && s.negative}`}>
                                        {formatChange(metrics.totalBookingsChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Impressions</span>
                                <Tooltip title={tooltips.impressions} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.totalImpressions || 0}</span>
                                    {!isNil(metrics.previousTotalImpressions) && (
                                        <span className={s.baseValue}>from {metrics.previousTotalImpressions}</span>
                                    )}
                                </div>
                                {!isNil(metrics.impressionsChangeRate) && (
                                    <div className={`${s.changeValue} ${metrics.impressionsChangeRate > 0 && s.positive || metrics.impressionsChangeRate < 0 && s.negative}`}>
                                        {formatChange(metrics.impressionsChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Impression share</span>
                                <Tooltip title={tooltips.impressionShare} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>{metrics.impressionShareRate || 0}%</span>
                                    {!isNil(metrics.previousImpressionShareRate) && (
                                        <span className={s.baseValue}>from {metrics.previousImpressionShareRate}%</span>
                                    )}
                                </div>
                                {!isNil(metrics.impressionShareChangeRate) && (
                                    <div className={`${s.changeValue} ${metrics.impressionShareChangeRate > 0 && s.positive || metrics.impressionShareChangeRate < 0 && s.negative}`}>
                                        {formatChange(metrics.impressionShareChangeRate)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <p className={s.updateNote}>
                        Reports automatically update every two weeks to reflect your latest performance.
                    </p>
                </Spin>
            </div>
        </Modal>
    );
};

export default ProListingReportModal;